/**
 * Account opening's API, typed at the edge of this package.
 *
 * Two clients in one file, and the split is the security property rather than
 * a convenience:
 *
 * **`PublicOnboardingApi`** is what an anonymous visitor's browser calls. It
 * takes the tenant in the path because there is no session to read one from,
 * and everything it returns is something the applicant typed — no id, no
 * decision, no internal code.
 *
 * **`OnboardingApi`** is the ADV's. It lists, opens, checks documents and
 * decides.
 */

import type { ApiClient } from "@hardauto/core-api";

const REVIEW = "/api/v1/onboarding";

export type RequestStatus = "submitted" | "under_review" | "incomplete" | "approved" | "rejected";

export type DocumentKind = "trade_register" | "bank_details" | "manager_id" | "other";

export type AuthMethod = "otp" | "password_mfa";

/** What the public form sends. Only two fields are required. */
export interface SubmitRequest {
  company_name: string;
  contact_email: string;
  contact_name?: string;
  contact_phone?: string;
  tax_id?: string;
  legal_form?: string;
  trade_register?: string;
  city?: string;
  address?: string;
  activity?: string;
  estimated_volume?: string;
  desired_site_code?: string;
}

/**
 * What the public form is told back.
 *
 * Deliberately thin: a reference, a status and the company's own name. No
 * `id` — an anonymous caller holding a sequential UUID could walk the table.
 */
export interface PublicRequest {
  reference: string;
  status: RequestStatus;
  company_name: string;
  submitted_at: string | null;
  documents_expected: DocumentKind[];
  documents_received: DocumentKind[];
}

export interface SubmittedDocument {
  id: string;
  kind: DocumentKind;
  filename: string;
  size: number;
  is_verified: boolean;
  verified_at: string | null;
}

/**
 * The gap, not a boolean.
 *
 * `missing` and `unverified` are different states with different remedies: an
 * e-mail to the applicant, or five minutes of the ADV's own time.
 */
export interface Completeness {
  required: DocumentKind[];
  verified: DocumentKind[];
  missing: DocumentKind[];
  unverified: DocumentKind[];
  is_complete: boolean;
  summary: string;
}

export interface RequestRow {
  id: string;
  reference: string;
  status: RequestStatus;
  company_name: string;
  tax_id: string;
  /** Whether the ICE has the expected shape. A flag, never a refusal. */
  ice_looks_valid: boolean;
  city: string;
  contact_name: string;
  contact_email: string;
  sales_rep_code: string;
  documents_summary: string;
  created_at: string | null;
}

export interface RequestList {
  items: RequestRow[];
  total: number;
  next_cursor: string | null;
  /** How many still need a person, whatever the current filter hides. */
  open_count: number;
}

export interface RequestDetail {
  id: string;
  reference: string;
  status: RequestStatus;
  company_name: string;
  tax_id: string;
  ice_looks_valid: boolean;
  legal_form: string;
  trade_register: string;
  city: string;
  address: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  activity: string;
  estimated_volume: string;
  desired_site_code: string;
  sales_rep_code: string;
  site_code: string;
  price_scale_code: string;
  credit_limit: string;
  auth_method: AuthMethod;
  decision_reason: string;
  decided_at: string | null;
  client_id: string | null;
  created_at: string | null;
  documents: SubmittedDocument[];
  completeness: Completeness;
}

export interface Decision {
  sales_rep_code: string;
  site_code?: string;
  price_scale_code?: string;
  credit_limit?: string;
  auth_method?: AuthMethod;
  /** Approve despite a missing document. Needs a second permission. */
  allow_incomplete?: boolean;
}

/** The public form's calls. Anonymous, with the tenant in the path. */
/** How a registry lookup ended. Three outcomes, and the form says three
 * different things: fill the fields, tell the applicant to check the number,
 * or say nothing at all. */
export type LookupOutcome = "found" | "absent" | "unavailable";

export interface CompanyLookup {
  outcome: LookupOutcome;
  tax_id: string;
  company_name: string;
  legal_form: string;
  trade_register: string;
  city: string;
  activity: string;
  /** The registry's own words — "EN ACTIVITE" and such. Shown, never parsed. */
  status: string;
}

export class PublicOnboardingApi {
  readonly #client: ApiClient;
  readonly #tenant: string;

  constructor(client: ApiClient, tenant: string) {
    this.#client = client;
    this.#tenant = tenant;
  }

  get #base(): string {
    return `${this.#root}/requests`;
  }

  get #root(): string {
    return `/api/v1/onboarding/${encodeURIComponent(this.#tenant)}`;
  }

  /**
   * Ask a public registry who holds this tax identifier.
   *
   * Answers 200 for every outcome, including "the registry is down" — so this
   * never rejects, and the caller reads `outcome`. That is deliberate: the
   * lookup is a convenience, and a prospect must be able to submit the form
   * when somebody else's server is having a bad day.
   */
  lookupCompany(ice: string): Promise<CompanyLookup> {
    return this.#client.request<CompanyLookup>(
      `${this.#root}/company-lookup?ice=${encodeURIComponent(ice)}`,
    );
  }

  submit(body: SubmitRequest): Promise<PublicRequest> {
    return this.#client.request<PublicRequest>(this.#base, { method: "POST", body });
  }

  /**
   * Attach a supporting document to an open request.
   *
   * Multipart through the client's `upload`, so the browser sets the
   * boundary — a `File` in a JSON body serialises to `{}`.
   */
  attach(reference: string, kind: DocumentKind, file: File): Promise<PublicRequest> {
    const form = new FormData();
    form.append("upload", file, file.name);
    form.append("kind", kind);
    return this.#client.upload<PublicRequest>(
      `${this.#base}/${encodeURIComponent(reference)}/documents`,
      form,
    );
  }

  /**
   * Where a request stands.
   *
   * A POST for a read, because the address is half the credential: in a query
   * string it would be written into every access log between the applicant
   * and the server.
   */
  status(reference: string, contactEmail: string): Promise<PublicRequest> {
    return this.#client.request<PublicRequest>(
      `${this.#base}/${encodeURIComponent(reference)}/status`,
      { method: "POST", body: { contact_email: contactEmail } },
    );
  }
}

/** The ADV's calls. */
export class OnboardingApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  list(
    options: {
      status?: RequestStatus;
      open_only?: boolean;
      search?: string;
      after_id?: string;
      limit?: number;
    } = {},
  ): Promise<RequestList> {
    const search = new URLSearchParams();
    // `request_status`, not `status`: the route's own parameter name, chosen
    // on the server so it cannot collide with FastAPI's `status` module in
    // the handler's signature.
    if (options.status) search.set("request_status", options.status);
    if (options.open_only === false) search.set("open_only", "false");
    if (options.search) search.set("search", options.search);
    if (options.after_id) search.set("after_id", options.after_id);
    search.set("limit", String(options.limit ?? 50));
    return this.#client.request<RequestList>(`${REVIEW}/requests?${search.toString()}`);
  }

  get(id: string): Promise<RequestDetail> {
    return this.#client.request<RequestDetail>(`${REVIEW}/requests/${encodeURIComponent(id)}`);
  }

  /** Mark it as being looked at, so two people do not review one dossier. */
  review(id: string): Promise<RequestDetail> {
    return this.#client.request<RequestDetail>(
      `${REVIEW}/requests/${encodeURIComponent(id)}/review`,
      { method: "POST" },
    );
  }

  verify(id: string, documentId: string, verified: boolean): Promise<RequestDetail> {
    return this.#client.request<RequestDetail>(
      `${REVIEW}/requests/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/verify`,
      { method: "POST", body: { verified } },
    );
  }

  /** The document's bytes, fetched with the session rather than linked. */
  document(id: string, documentId: string): Promise<Blob> {
    return this.#client.requestBlob(
      `${REVIEW}/requests/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}`,
    );
  }

  requestDocuments(id: string): Promise<RequestDetail> {
    return this.#client.request<RequestDetail>(
      `${REVIEW}/requests/${encodeURIComponent(id)}/documents-needed`,
      { method: "POST" },
    );
  }

  approve(id: string, decision: Decision): Promise<RequestDetail> {
    return this.#client.request<RequestDetail>(
      `${REVIEW}/requests/${encodeURIComponent(id)}/approve`,
      { method: "POST", body: decision },
    );
  }

  reject(id: string, reason: string): Promise<RequestDetail> {
    return this.#client.request<RequestDetail>(
      `${REVIEW}/requests/${encodeURIComponent(id)}/reject`,
      { method: "POST", body: { reason } },
    );
  }
}
